// ============================================================================
// landingContentDocs — landing pages POR DOCUMENTO (Fase C, Tier 2).
// ----------------------------------------------------------------------------
// Alta intenção de compra (a pessoa que busca por um documento específico está
// perto de contratar). Mesmo template/sistema das Tier 1; mesclado em
// lib/landingContent. Slug em PT com prefixo de idioma (/…, /es/…, /en/…).
// ============================================================================

export const DOC_PAGES = [
  { key: "doc-birth", path: "/traducao-certidao-de-nascimento" },
  { key: "doc-diploma", path: "/traducao-diploma-e-historico" },
  { key: "doc-criminal", path: "/traducao-antecedentes-criminais" },
  { key: "doc-license", path: "/traducao-carteira-de-habilitacao" },
  { key: "doc-marriage", path: "/traducao-certidao-de-casamento" },
];

export const DOC_NAV = {
  "doc-birth": { pt: "Certidão de Nascimento", es: "Acta de Nacimiento", en: "Birth Certificate" },
  "doc-diploma": { pt: "Diploma e Histórico", es: "Diploma y Expediente", en: "Diploma & Transcript" },
  "doc-criminal": { pt: "Antecedentes Criminais", es: "Antecedentes Penales", en: "Background Check" },
  "doc-license": { pt: "Carteira de Habilitação", es: "Licencia de Conducir", en: "Driver's License" },
  "doc-marriage": { pt: "Certidão de Casamento", es: "Acta de Matrimonio", en: "Marriage Certificate" },
};

// Benefícios de digital/rápido são comuns; o do meio é tailored por documento.
const fast = {
  pt: { title: "Rápida e digital", text: "Orçamento na hora e entrega digital com assinatura válida; opção de prioridade." },
  es: { title: "Rápida y digital", text: "Presupuesto al instante y entrega digital con firma válida; opción de prioridad." },
  en: { title: "Fast and digital", text: "Instant quote and digital delivery with a valid signature; priority available." },
};
const uscis = {
  pt: { title: "Aceita pelo USCIS", text: "Tradução certificada em inglês, no padrão exigido em imigração e green card." },
  es: { title: "Aceptada por USCIS", text: "Traducción certificada al inglés, en el formato exigido en inmigración y green card." },
  en: { title: "USCIS-accepted", text: "Certified English translation, in the format required for immigration and green card." },
};
const price = {
  pt: { q: "Quanto custa?", a: "O valor é por página e aparece no orçamento instantâneo ao enviar o documento." },
  es: { q: "¿Cuánto cuesta?", a: "El valor es por página y aparece en el presupuesto instantáneo al enviar el documento." },
  en: { q: "How much does it cost?", a: "Pricing is per page and appears in your instant quote when you upload the document." },
};

export const DOC_CONTENT = {
  "doc-birth": {
    pt: {
      title: "Tradução de Certidão de Nascimento (Juramentada e Certificada)",
      description: "Tradução juramentada e certificada da certidão de nascimento, aceita pelo USCIS e por órgãos oficiais. Entrega digital e orçamento na hora.",
      h1: "Tradução de Certidão de Nascimento",
      subtitle: "Juramentada para o Brasil e certificada (USCIS) para os Estados Unidos.",
      intro: "A certidão de nascimento é um dos documentos mais pedidos em imigração, cidadania e matrícula no exterior. Traduzimos e certificamos no formato aceito pelo USCIS e por órgãos oficiais, com entrega digital.",
      benefits: [uscis.pt, { title: "Fiel ao original", text: "Selos, carimbos e anotações traduzidos com precisão por tradutores membros da ATA." }, fast.pt],
      ctaLabel: "Orçar tradução da certidão",
      faq: [
        { q: "A tradução da certidão de nascimento é aceita pelo USCIS?", a: "Sim. Entregamos a tradução certificada em inglês, com certificação assinada, no formato exigido pelo USCIS." },
        { q: "Preciso da certidão atualizada ou em inteiro teor?", a: "Recomenda-se a versão mais recente. Na dúvida, orientamos você antes de traduzir." },
        price.pt,
      ],
    },
    es: {
      title: "Traducción del Acta de Nacimiento (Certificada)",
      description: "Traducción certificada del acta o partida de nacimiento, aceptada por USCIS y autoridades. Entrega digital y presupuesto al instante.",
      h1: "Traducción del Acta de Nacimiento",
      subtitle: "Certificada y aceptada por USCIS para inmigración.",
      intro: "El acta de nacimiento es uno de los documentos más solicitados en inmigración, ciudadanía y estudios en el exterior. La traducimos y certificamos en el formato aceptado por USCIS y autoridades, con entrega digital.",
      benefits: [uscis.es, { title: "Fiel al original", text: "Sellos, timbres y anotaciones traducidos con precisión por traductores miembros de la ATA." }, fast.es],
      ctaLabel: "Cotizar traducción del acta",
      faq: [
        { q: "¿La traducción del acta de nacimiento es aceptada por USCIS?", a: "Sí. Entregamos la traducción certificada al inglés, con certificación firmada, en el formato exigido por USCIS." },
        { q: "¿Necesito el acta actualizada?", a: "Se recomienda la versión más reciente. Si tienes dudas, te orientamos antes de traducir." },
        price.es,
      ],
    },
    en: {
      title: "Birth Certificate Translation (Certified)",
      description: "Certified birth certificate translation, accepted by USCIS and authorities. Digital delivery and instant quote.",
      h1: "Birth Certificate Translation",
      subtitle: "Certified and USCIS-accepted for immigration.",
      intro: "The birth certificate is one of the most requested documents for immigration, citizenship and studying abroad. We translate and certify it in the format accepted by USCIS and authorities, with digital delivery.",
      benefits: [uscis.en, { title: "True to the original", text: "Seals, stamps and notes translated accurately by ATA-member translators." }, fast.en],
      ctaLabel: "Quote my certificate translation",
      faq: [
        { q: "Is the birth certificate translation accepted by USCIS?", a: "Yes. We deliver the certified English translation, with a signed certification, in the format USCIS requires." },
        { q: "Do I need an up-to-date certificate?", a: "The most recent version is recommended. If unsure, we'll guide you before translating." },
        price.en,
      ],
    },
  },

  "doc-diploma": {
    pt: {
      title: "Tradução de Diploma e Histórico Escolar (Juramentada e Certificada)",
      description: "Tradução juramentada e certificada de diploma e histórico escolar para universidades, revalidação e imigração. Entrega digital e orçamento na hora.",
      h1: "Tradução de Diploma e Histórico Escolar",
      subtitle: "Para universidades no exterior, revalidação e imigração.",
      intro: "Diplomas e históricos escolares precisam de tradução oficial para candidaturas a universidades, revalidação de curso e processos de imigração. Traduzimos com precisão técnica dos termos acadêmicos.",
      benefits: [uscis.pt, { title: "Precisão acadêmica", text: "Disciplinas, notas, carga horária e títulos traduzidos com rigor por tradutores membros da ATA." }, fast.pt],
      ctaLabel: "Orçar tradução do diploma",
      faq: [
        { q: "Serve para aplicar em universidade no exterior?", a: "Sim. Entregamos a tradução certificada aceita por universidades e por processos de admissão e revalidação." },
        { q: "Vocês traduzem histórico com ementas?", a: "Sim, incluindo histórico, ementas e conteúdo programático quando necessário. Orçamos por página." },
        price.pt,
      ],
    },
    es: {
      title: "Traducción de Diploma y Expediente Académico (Certificada)",
      description: "Traducción certificada de diploma y expediente académico para universidades, convalidación e inmigración. Entrega digital y presupuesto al instante.",
      h1: "Traducción de Diploma y Expediente Académico",
      subtitle: "Para universidades en el exterior, convalidación e inmigración.",
      intro: "Los diplomas y expedientes académicos requieren traducción oficial para postulaciones a universidades, convalidación de estudios y trámites de inmigración. Traducimos con precisión los términos académicos.",
      benefits: [uscis.es, { title: "Precisión académica", text: "Asignaturas, notas, carga horaria y títulos traducidos con rigor por traductores miembros de la ATA." }, fast.es],
      ctaLabel: "Cotizar traducción del diploma",
      faq: [
        { q: "¿Sirve para postular a una universidad en el exterior?", a: "Sí. Entregamos la traducción certificada aceptada por universidades y por procesos de admisión y convalidación." },
        { q: "¿Traducen el expediente con los programas de estudio?", a: "Sí, incluyendo expediente y contenidos programáticos cuando es necesario. Cotizamos por página." },
        price.es,
      ],
    },
    en: {
      title: "Diploma and Transcript Translation (Certified)",
      description: "Certified translation of diplomas and academic transcripts for universities, credential evaluation and immigration. Digital delivery and instant quote.",
      h1: "Diploma and Transcript Translation",
      subtitle: "For universities abroad, credential evaluation and immigration.",
      intro: "Diplomas and transcripts require official translation for university applications, credential evaluation and immigration. We translate academic terminology with precision.",
      benefits: [uscis.en, { title: "Academic precision", text: "Courses, grades, credit hours and titles translated rigorously by ATA-member translators." }, fast.en],
      ctaLabel: "Quote my diploma translation",
      faq: [
        { q: "Is it accepted for university applications abroad?", a: "Yes. We deliver certified translation accepted by universities and by admission and credential-evaluation processes." },
        { q: "Do you translate transcripts with course descriptions?", a: "Yes, including transcripts and syllabi when needed. We quote per page." },
        price.en,
      ],
    },
  },

  "doc-criminal": {
    pt: {
      title: "Tradução de Antecedentes Criminais (Certificada para USCIS)",
      description: "Tradução certificada de antecedentes criminais para vistos e imigração, aceita pelo USCIS. Entrega digital e orçamento na hora.",
      h1: "Tradução de Antecedentes Criminais",
      subtitle: "Documento exigido em vistos e processos de imigração.",
      intro: "A certidão de antecedentes criminais é frequentemente exigida em pedidos de visto e imigração. Traduzimos e certificamos no formato aceito pelo USCIS e por consulados, com total sigilo.",
      benefits: [uscis.pt, { title: "Sigilo total", text: "Documento sensível tratado com confidencialidade por tradutores membros da ATA." }, fast.pt],
      ctaLabel: "Orçar tradução dos antecedentes",
      faq: [
        { q: "A tradução é aceita no consulado e pelo USCIS?", a: "Sim. Entregamos a tradução certificada em inglês, no formato aceito em processos consulares e de imigração." },
        { q: "Consigo com urgência?", a: "Sim, há opção de prioridade. O prazo aparece no orçamento, conforme o número de páginas." },
        price.pt,
      ],
    },
    es: {
      title: "Traducción de Antecedentes Penales (Certificada para USCIS)",
      description: "Traducción certificada de antecedentes penales para visas e inmigración, aceptada por USCIS. Entrega digital y presupuesto al instante.",
      h1: "Traducción de Antecedentes Penales",
      subtitle: "Documento exigido en visas y trámites de inmigración.",
      intro: "El certificado de antecedentes penales suele exigirse en solicitudes de visa e inmigración. Lo traducimos y certificamos en el formato aceptado por USCIS y consulados, con total confidencialidad.",
      benefits: [uscis.es, { title: "Confidencialidad total", text: "Documento sensible tratado con confidencialidad por traductores miembros de la ATA." }, fast.es],
      ctaLabel: "Cotizar traducción de antecedentes",
      faq: [
        { q: "¿La traducción es aceptada en el consulado y por USCIS?", a: "Sí. Entregamos la traducción certificada al inglés, en el formato aceptado en procesos consulares y de inmigración." },
        { q: "¿Puedo obtenerla con urgencia?", a: "Sí, hay opción de prioridad. El plazo aparece en el presupuesto, según el número de páginas." },
        price.es,
      ],
    },
    en: {
      title: "Criminal Background Check Translation (Certified for USCIS)",
      description: "Certified translation of criminal background checks for visas and immigration, accepted by USCIS. Digital delivery and instant quote.",
      h1: "Criminal Background Check Translation",
      subtitle: "A document required for visas and immigration processes.",
      intro: "Criminal background checks are often required in visa and immigration applications. We translate and certify them in the format accepted by USCIS and consulates, with full confidentiality.",
      benefits: [uscis.en, { title: "Full confidentiality", text: "Sensitive documents handled confidentially by ATA-member translators." }, fast.en],
      ctaLabel: "Quote my background check",
      faq: [
        { q: "Is the translation accepted at consulates and by USCIS?", a: "Yes. We deliver the certified English translation, in the format accepted in consular and immigration processes." },
        { q: "Can I get it urgently?", a: "Yes, a priority option is available. Turnaround is shown in the quote, based on page count." },
        price.en,
      ],
    },
  },

  "doc-license": {
    pt: {
      title: "Tradução de Carteira de Habilitação / CNH (Certificada)",
      description: "Tradução certificada da carteira de habilitação (CNH) para dirigir, alugar carro e usar no exterior. Entrega digital e orçamento na hora.",
      h1: "Tradução de Carteira de Habilitação (CNH)",
      subtitle: "Para dirigir, alugar carro e usar oficialmente no exterior.",
      intro: "A tradução da CNH é usada para dirigir e alugar veículos no exterior e em processos oficiais. Entregamos a tradução certificada, no formato aceito por autoridades, com rapidez.",
      benefits: [{ title: "Pronta para uso", text: "Tradução certificada aceita por autoridades e locadoras no exterior." }, { title: "Feita por especialistas", text: "Tradutores membros da ATA e revisores nativos." }, fast.pt],
      ctaLabel: "Orçar tradução da CNH",
      faq: [
        { q: "A tradução da CNH serve para alugar carro no exterior?", a: "Sim. A tradução certificada é aceita por autoridades e locadoras; recomenda-se levá-la junto com a CNH original." },
        { q: "Preciso da PID (Permissão Internacional)?", a: "Depende do país. A tradução certificada atende muitos casos; orientamos conforme o destino." },
        price.pt,
      ],
    },
    es: {
      title: "Traducción de Licencia de Conducir (Certificada)",
      description: "Traducción certificada de la licencia de conducir para conducir, alquilar auto y usar en el exterior. Entrega digital y presupuesto al instante.",
      h1: "Traducción de Licencia de Conducir",
      subtitle: "Para conducir, alquilar auto y usar oficialmente en el exterior.",
      intro: "La traducción de la licencia de conducir se usa para conducir y alquilar vehículos en el exterior y en trámites oficiales. Entregamos la traducción certificada, en el formato aceptado por autoridades, con rapidez.",
      benefits: [{ title: "Lista para usar", text: "Traducción certificada aceptada por autoridades y arrendadoras en el exterior." }, { title: "Hecha por especialistas", text: "Traductores miembros de la ATA y revisores nativos." }, fast.es],
      ctaLabel: "Cotizar traducción de la licencia",
      faq: [
        { q: "¿La traducción sirve para alquilar auto en el exterior?", a: "Sí. La traducción certificada es aceptada por autoridades y arrendadoras; se recomienda llevarla junto con la licencia original." },
        { q: "¿Necesito el permiso internacional?", a: "Depende del país. La traducción certificada cubre muchos casos; te orientamos según el destino." },
        price.es,
      ],
    },
    en: {
      title: "Driver's License Translation (Certified)",
      description: "Certified driver's license translation to drive, rent a car and use abroad. Digital delivery and instant quote.",
      h1: "Driver's License Translation",
      subtitle: "To drive, rent a car and use officially abroad.",
      intro: "A driver's license translation is used to drive and rent vehicles abroad and in official processes. We deliver the certified translation, in the format accepted by authorities, quickly.",
      benefits: [{ title: "Ready to use", text: "Certified translation accepted by authorities and rental companies abroad." }, { title: "By specialists", text: "ATA-member translators and native reviewers." }, fast.en],
      ctaLabel: "Quote my license translation",
      faq: [
        { q: "Is the translation valid to rent a car abroad?", a: "Yes. The certified translation is accepted by authorities and rental companies; carry it together with your original license." },
        { q: "Do I need an International Driving Permit?", a: "It depends on the country. The certified translation covers many cases; we'll advise based on your destination." },
        price.en,
      ],
    },
  },

  "doc-marriage": {
    pt: {
      title: "Tradução de Certidão de Casamento (Juramentada e Certificada)",
      description: "Tradução juramentada e certificada da certidão de casamento para imigração, green card e uso oficial. Entrega digital e orçamento na hora.",
      h1: "Tradução de Certidão de Casamento",
      subtitle: "Para green card por casamento, imigração e uso oficial.",
      intro: "A certidão de casamento é essencial em pedidos de green card por casamento e em outros processos de imigração. Traduzimos e certificamos no formato aceito pelo USCIS e por órgãos oficiais.",
      benefits: [uscis.pt, { title: "Fiel ao original", text: "Selos, carimbos e averbações traduzidos com precisão por tradutores membros da ATA." }, fast.pt],
      ctaLabel: "Orçar tradução da certidão",
      faq: [
        { q: "Serve para green card por casamento?", a: "Sim. Entregamos a tradução certificada em inglês, no formato exigido pelo USCIS nesses processos." },
        { q: "E se houver averbações (divórcio, óbito)?", a: "Traduzimos a certidão com as averbações. O valor é por página, informado no orçamento." },
        price.pt,
      ],
    },
    es: {
      title: "Traducción del Acta de Matrimonio (Certificada)",
      description: "Traducción certificada del acta de matrimonio para inmigración, green card y uso oficial. Entrega digital y presupuesto al instante.",
      h1: "Traducción del Acta de Matrimonio",
      subtitle: "Para green card por matrimonio, inmigración y uso oficial.",
      intro: "El acta de matrimonio es esencial en solicitudes de green card por matrimonio y en otros trámites de inmigración. La traducimos y certificamos en el formato aceptado por USCIS y autoridades.",
      benefits: [uscis.es, { title: "Fiel al original", text: "Sellos, timbres y anotaciones traducidos con precisión por traductores miembros de la ATA." }, fast.es],
      ctaLabel: "Cotizar traducción del acta",
      faq: [
        { q: "¿Sirve para green card por matrimonio?", a: "Sí. Entregamos la traducción certificada al inglés, en el formato exigido por USCIS en esos procesos." },
        { q: "¿Y si hay anotaciones (divorcio, defunción)?", a: "Traducimos el acta con las anotaciones. El valor es por página, informado en el presupuesto." },
        price.es,
      ],
    },
    en: {
      title: "Marriage Certificate Translation (Certified)",
      description: "Certified marriage certificate translation for immigration, green card and official use. Digital delivery and instant quote.",
      h1: "Marriage Certificate Translation",
      subtitle: "For marriage-based green card, immigration and official use.",
      intro: "The marriage certificate is essential for marriage-based green card applications and other immigration processes. We translate and certify it in the format accepted by USCIS and authorities.",
      benefits: [uscis.en, { title: "True to the original", text: "Seals, stamps and annotations translated accurately by ATA-member translators." }, fast.en],
      ctaLabel: "Quote my certificate translation",
      faq: [
        { q: "Is it valid for a marriage-based green card?", a: "Yes. We deliver the certified English translation, in the format USCIS requires for these processes." },
        { q: "What if there are annotations (divorce, death)?", a: "We translate the certificate with its annotations. Pricing is per page, shown in your quote." },
        price.en,
      ],
    },
  },
};
